import { useState } from 'react';
import { type Language, t } from '../i18n';
import type { StartupFormData } from '../hooks/useChat';

interface StartupFormProps {
  language: Language;
  onSubmit: (data: StartupFormData, contactEmail?: string) => void;
}

const PROJECT_STAGES = ['idea', 'concept', 'prototype', 'mvp', 'revenue'] as const;
const FOUNDER_BACKGROUNDS = ['technical', 'business', 'domain', 'mixed'] as const;
const TEAM_SIZES = ['solo', '2-3', '4-10', '10+'] as const;
const INVESTMENT_READINESS = ['bootstrapping', 'seeking-seed', 'have-funding', 'self-funded'] as const;
const TIMELINES = ['exploring', '3-6-months', 'actively-building'] as const;
const PRIMARY_GEOGRAPHIES = ['Italy', 'Europe', 'Global'];
const OTHER_GEOGRAPHIES = ['US', 'LATAM', 'MENA', 'East Europe', 'Asia'];

const SECTORS = [
  'Technology/SaaS',
  'Finance/Banking',
  'Retail/E-commerce',
  'Healthcare',
  'Education',
  'Tourism/Hospitality',
  'Food/Agriculture',
  'Fashion/Textile',
  'Manufacturing',
  'Energy/Utilities',
  'Real Estate',
  'Media/Entertainment',
  'Professional Services',
  'Other',
];

export function StartupForm({ language, onSubmit }: StartupFormProps) {
  const [formData, setFormData] = useState<StartupFormData>({
    ideaDescription: '',
    projectStage: 'idea',
    targetSector: '',
    founderBackground: 'technical',
    teamSize: 'solo',
    investmentReadiness: 'bootstrapping',
    geographicFocus: [],
    timeline: 'exploring',
    hasExistingProduct: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof StartupFormData, boolean>>>({});
  const [showOtherGeo, setShowOtherGeo] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<Record<keyof StartupFormData, boolean>> = {};
    if (!formData.ideaDescription.trim()) newErrors.ideaDescription = true;
    if (!formData.targetSector) newErrors.targetSector = true;
    if (formData.geographicFocus.length === 0) newErrors.geographicFocus = true;

    const trimmedEmail = contactEmail.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData, trimmedEmail || undefined);
  };

  const handleGeoToggle = (geo: string) => {
    setFormData((prev) => ({
      ...prev,
      geographicFocus: prev.geographicFocus.includes(geo)
        ? prev.geographicFocus.filter((g) => g !== geo)
        : [...prev.geographicFocus, geo],
    }));
    setErrors((prev) => ({ ...prev, geographicFocus: false }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="relative mb-8 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-primary-50 dark:bg-primary-50/10" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100/60 rounded-full blur-3xl dark:bg-primary-100/10" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-100/40 rounded-full blur-2xl dark:bg-primary-100/5" />

        <div className="relative z-10 px-8 py-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t(language, 'startup.hero.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t(language, 'startup.hero.subtitle')}
          </p>

          <div className="flex justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">
                {t(language, 'startup.hero.stat1')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(language, 'startup.hero.stat1Label')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">
                {t(language, 'startup.hero.stat2')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(language, 'startup.hero.stat2Label')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">
                {t(language, 'startup.hero.stat3')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(language, 'startup.hero.stat3Label')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-6">
        <h2 className="font-semibold text-foreground mb-6 text-lg">
          {t(language, 'startup.form.title')}
        </h2>

        {/* Idea Description */}
        <div className="mb-6">
          <label htmlFor="ideaDescription" className="block text-xs font-medium text-foreground mb-1">
            {t(language, 'startup.form.ideaDescription.label')} *
          </label>
          <textarea
            id="ideaDescription"
            value={formData.ideaDescription}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, ideaDescription: e.target.value }));
              setErrors((prev) => ({ ...prev, ideaDescription: false }));
            }}
            rows={3}
            maxLength={2000}
            className={`w-full px-3 py-2 text-sm border rounded-lg bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary resize-none ${
              errors.ideaDescription ? 'border-red-500' : 'border-border'
            }`}
            placeholder={t(language, 'startup.form.ideaDescription.placeholder')}
          />
          <div className="text-xs text-muted-foreground mt-1 text-right">
            {formData.ideaDescription.length}/2000
          </div>
        </div>

        {/* Row: Stage, Sector, Background */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="projectStage" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'startup.form.projectStage.label')}
            </label>
            <select
              id="projectStage"
              value={formData.projectStage}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                projectStage: e.target.value as StartupFormData['projectStage'],
              }))}
              className="form-select text-sm py-2"
            >
              {PROJECT_STAGES.map((stage) => (
                <option key={stage} value={stage}>
                  {t(language, `startup.form.projectStage.options.${stage}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="targetSector" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'startup.form.targetSector.label')} *
            </label>
            <select
              id="targetSector"
              value={formData.targetSector}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, targetSector: e.target.value }));
                setErrors((prev) => ({ ...prev, targetSector: false }));
              }}
              className={`form-select text-sm py-2 ${errors.targetSector ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'startup.form.targetSector.placeholder')}</option>
              {SECTORS.map((sector) => (
                <option key={sector} value={sector}>
                  {t(language, `form.industry.options.${sector}`) !== `form.industry.options.${sector}`
                    ? t(language, `form.industry.options.${sector}`)
                    : sector}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="founderBackground" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'startup.form.founderBackground.label')}
            </label>
            <select
              id="founderBackground"
              value={formData.founderBackground}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                founderBackground: e.target.value as StartupFormData['founderBackground'],
              }))}
              className="form-select text-sm py-2"
            >
              {FOUNDER_BACKGROUNDS.map((bg) => (
                <option key={bg} value={bg}>
                  {t(language, `startup.form.founderBackground.options.${bg}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row: Team Size, Investment, Timeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div>
            <label htmlFor="teamSize" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'startup.form.teamSize.label')}
            </label>
            <select
              id="teamSize"
              value={formData.teamSize}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                teamSize: e.target.value as StartupFormData['teamSize'],
              }))}
              className="form-select text-sm py-2"
            >
              {TEAM_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t(language, `startup.form.teamSize.options.${size}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="investmentReadiness" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'startup.form.investmentReadiness.label')}
            </label>
            <select
              id="investmentReadiness"
              value={formData.investmentReadiness}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                investmentReadiness: e.target.value as StartupFormData['investmentReadiness'],
              }))}
              className="form-select text-sm py-2"
            >
              {INVESTMENT_READINESS.map((ir) => (
                <option key={ir} value={ir}>
                  {t(language, `startup.form.investmentReadiness.options.${ir}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="timeline" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'startup.form.timeline.label')}
            </label>
            <select
              id="timeline"
              value={formData.timeline}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                timeline: e.target.value as StartupFormData['timeline'],
              }))}
              className="form-select text-sm py-2"
            >
              {TIMELINES.map((tl) => (
                <option key={tl} value={tl}>
                  {t(language, `startup.form.timeline.options.${tl}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Geographic Focus */}
        <fieldset className="mb-4">
          <legend className="block text-xs font-medium text-foreground mb-2">
            {t(language, 'startup.form.geographicFocus.label')} *
          </legend>
          <div className="flex flex-wrap gap-2">
            {PRIMARY_GEOGRAPHIES.map((geo) => (
              <label
                key={geo}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                  formData.geographicFocus.includes(geo)
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-card border-border text-foreground hover:border-primary/50'
                } ${errors.geographicFocus ? 'border-destructive' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={formData.geographicFocus.includes(geo)}
                  onChange={() => handleGeoToggle(geo)}
                  className="w-3.5 h-3.5 accent-primary rounded"
                />
                <span>{t(language, `form.geography.options.${geo}`)}</span>
              </label>
            ))}
            <label
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                showOtherGeo
                  ? 'bg-muted border-muted-foreground/50 text-foreground'
                  : 'bg-card border-border text-foreground hover:border-primary/50'
              }`}
            >
              <input
                type="checkbox"
                checked={showOtherGeo}
                onChange={() => setShowOtherGeo(!showOtherGeo)}
                className="w-3.5 h-3.5 accent-primary rounded"
              />
              <span>{t(language, 'form.geography.other')}</span>
            </label>
          </div>
          {showOtherGeo && (
            <div className="mt-3 pl-4 border-l-2 border-border">
              <div className="flex flex-wrap gap-2">
                {OTHER_GEOGRAPHIES.map((geo) => (
                  <label
                    key={geo}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                      formData.geographicFocus.includes(geo)
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-card border-border text-foreground hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.geographicFocus.includes(geo)}
                      onChange={() => handleGeoToggle(geo)}
                      className="w-3.5 h-3.5 accent-primary rounded"
                    />
                    <span>{t(language, `form.geography.options.${geo}`)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </fieldset>

        {/* Has Existing Product */}
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.hasExistingProduct}
              onChange={(e) => setFormData((prev) => ({ ...prev, hasExistingProduct: e.target.checked }))}
              className="w-4 h-4 accent-primary rounded"
            />
            <span className="text-sm text-foreground">
              {t(language, 'startup.form.hasExistingProduct.label')}
            </span>
          </label>
        </div>

        {/* Optional contact email (captures partial leads) */}
        <fieldset>
          <label className="text-sm font-medium mb-1 block">
            {language === 'it' ? 'Email (opzionale)' : 'Email (optional)'}
          </label>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              setEmailError(false);
            }}
            placeholder={language === 'it' ? 'tu@azienda.it' : 'you@company.com'}
            className={`form-input w-full ${emailError ? 'border-red-500' : ''}`}
          />
          <p className="text-xs text-muted mt-1">
            {language === 'it'
              ? 'Ti invieremo il report via email quando sarà pronto. Puoi anche lasciarla dopo.'
              : 'We\'ll send the report to this address when ready. You can also add it later.'}
          </p>
          {emailError && (
            <p className="text-xs text-red-500 mt-1">
              {language === 'it' ? 'Formato email non valido' : 'Invalid email format'}
            </p>
          )}
        </fieldset>

        {/* Submit */}
        <button type="submit" className="btn-primary w-full py-3 text-base font-medium">
          {t(language, 'startup.form.submit')}
        </button>
      </form>
    </div>
  );
}
